import { useMutation, useQuery } from '@tanstack/react-query';
import {
    ChangeEvent,
    ForwardedRef,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';
import { z } from 'zod'; // Add Zod for validation
import { useAlert } from '../../../../AlertProvider';
import Autocomplete from '../../../../components/Autocomplete';
import Input from '../../../../components/Input';
import Loader from '../../../../components/Loader';
import TextEditor from '../../../../components/TextEditor';
import { alertType } from '../../../../constants/alertTypeConstant';
import { successStatus } from '../../../../constants/requestStatusContants';
import OptionInterface from '../../../../interfaces/OptionInterface';
import { fetchClientCases } from '../../../../services/caseService';
import { fetchContactClient } from '../../../../services/contactClientService';
import {
    deleteNote,
    fetchNoteById,
    postNote,
    updateNote,
} from '../../../../services/noteService';
import CaseType from '../../../../types/CaseType';
import NoteFormType from '../../../../types/NoteFormType';
import UserType from '../../../../types/UserType';
import styles from './style.module.scss';

// Define validation schema
const noteSchema = z.object({
    title: z.string().min(1, 'Le titre est requis'),
    text: z.string().min(1, 'Le contenu est requis'),
    clientCaseId: z.string().optional(),
    clientId: z.string().optional(),
});

type ValidationErrors = {
    [key: string]: string;
};

interface Props {
    noteId: string;
}

export interface NoteDetailsHandle {
    saveSelectedNote: () => void;
    deleteSelectedNote: () => void;
}

const emptyNote = {
    title: '',
    text: '',
};

function NoteDetails(props: Props, ref: ForwardedRef<NoteDetailsHandle>) {
    const { noteId } = props;
    const alert = useAlert();

    const [noteToSave, setNoteToSave] = useState<NoteFormType>(emptyNote);
    const [clientCaseId, setClientCaseId] = useState<string | undefined>(
        undefined
    );
    const [clientId, setClientId] = useState<string | undefined>(undefined);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (noteId.length === 0) {
            setNoteToSave(emptyNote);
        }
    }, [setNoteToSave, noteId]);

    const { data: clientsCases, isPending: clientCaseIsPending } = useQuery({
        queryKey: ['getClientsCases'],
        queryFn: async () => {
            const { data } = await fetchClientCases();
            return data;
        },
    });

    const { data: contacts, isPending: contactIsPending } = useQuery({
        queryKey: ['getContacts'],
        queryFn: async () => {
            const { data } = await fetchContactClient();
            return data;
        },
    });

    const { data: note, isPending } = useQuery({
        queryKey: ['getUser', noteId],
        queryFn: async () => {
            if (noteId?.length > 0) {
                const { data } = await fetchNoteById(noteId);
                setNoteToSave(data);
                return data;
            }
            return null;
        },
    });

    const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setNoteToSave({ ...noteToSave, ...{ title: value } });
    };
    const onTextChange = (value: string) => {
        setNoteToSave({ ...noteToSave, ...{ text: value } });
    };

    const onChange = () => {
        setClientCaseId(undefined);
        setClientId(undefined);
    };
    const onClientCaseSelect = (value?: OptionInterface) => {
        setClientCaseId(value?.data.id);
        setClientId(value?.data.userId);
    };
    const onContactSelect = (value?: OptionInterface) => {
        setClientId(value?.data.id);
    };

    const clientsCasesOptions = clientsCases?.map((clientCase: CaseType) => {
        const item: OptionInterface = {
            label: clientCase.caseName || clientCase.caseRef,
            name: clientCase.caseName || clientCase.caseRef,
            id: clientCase.id,
            data: clientCase,
        };
        return item;
    });

    const contactsOptions = contacts?.map((contact: UserType) => {
        const item: OptionInterface = {
            label: contact.firstName + ' ' + contact.lastName,
            name: contact.firstName + ' ' + contact.lastName,
            id: contact.id,
            data: contact,
        };
        return item;
    });

    const validateNote = (): boolean => {
        try {
            noteSchema.parse({
                ...noteToSave,
                clientCaseId,
                clientId,
            });
            setErrors({});
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                const formattedErrors: ValidationErrors = {};
                error.errors.forEach((err) => {
                    if (err.path) {
                        formattedErrors[err.path[0]] = err.message;
                    }
                });
                setErrors(formattedErrors);
            }
            return false;
        }
    };

    const { mutate: saveNote } = useMutation({
        mutationFn: async () => {
            if (!validateNote()) {
                alert.show({
                    children:
                        'Veuillez corriger les erreurs avant de sauvegarder',
                    type: alertType.error,
                });
                return;
            }

            setIsSubmitting(true);
            try {
                if (noteId?.length > 0) {
                    const { data, status } = await updateNote(
                        noteId,
                        noteToSave
                    );
                    handleResponse(status, 'enregistré');
                    return data;
                } else {
                    const { data, status } = await postNote(noteToSave);
                    handleResponse(status, 'créé');
                    return data;
                }
            } catch (error) {
                handleError(error);
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const handleResponse = (status: number, action: string) => {
        if (successStatus.includes(status)) {
            alert.show({
                children: `Votre note a été ${action} avec succès`,
                type: alertType[status],
            });
        } else {
            alert.show({
                children:
                    'Une erreur est survenue. Veuillez réessayer plus tard.',
                type: alertType[status],
            });
        }
    };

    const handleError = (error: unknown) => {
        console.error('Error saving note:', error);
        alert.show({
            children: 'Une erreur inattendue est survenue. Veuillez réessayer.',
            type: alertType.error,
        });
    };

    const { mutate: deleteSelectedNote } = useMutation({
        mutationFn: async (noteId: string) => {
            if (!!noteId && noteId.length > 0) {
                const { status } = await deleteNote(noteId);

                if (successStatus.includes(status)) {
                    alert.show({
                        children: 'Votre note a été supprimé avec succès',
                        type: alertType[status],
                    });
                } else {
                    alert.show({
                        children:
                            'Erreur! Une erreur est survenue. Veuillez réessayer plus tard.',
                        type: alertType[status],
                    });
                }
            }
        },
    });

    useImperativeHandle(ref, () => ({
        saveSelectedNote() {
            saveNote();
        },
        deleteSelectedNote() {
            deleteSelectedNote(noteId);
        },
    }));

    if (isPending) {
        return <Loader />;
    }

    return (
        <div className={styles.container}>
            <Input
                label="Titre"
                name="name"
                placeholder="Nouvelle note"
                onChange={onTitleChange}
                value={noteToSave?.title}
                error={errors.title}
                isRequired
            />
            <div className={styles.autocompleteContainer}>
                <Autocomplete
                    options={clientsCasesOptions}
                    label="Dossier"
                    onOptionClick={onClientCaseSelect}
                    onChange={onChange}
                    name="clientCase"
                    value={note?.clientCaseId}
                    isDataLoading={clientCaseIsPending}
                />
                <Autocomplete
                    options={contactsOptions}
                    label="Client"
                    onOptionClick={onContactSelect}
                    disabled={clientCaseId !== undefined}
                    value={note?.clientId || clientId}
                    onChange={onChange}
                    name="client"
                    isDataLoading={contactIsPending}
                />
            </div>

            <TextEditor
                onChange={onTextChange}
                value={noteToSave?.text}
                error={errors.text}
                required
            />

            {isSubmitting && <Loader />}
        </div>
    );
}

export default forwardRef<NoteDetailsHandle, Props>(NoteDetails);
